(function () {
  function createCoveoCardsModule(deps) {
    const searchPageResultsPerPage = Number(
      deps?.searchPageResultsPerPage || 32,
    );
    const getProductField =
      typeof deps?.getProductField === "function"
        ? deps.getProductField
        : function () {
          return null;
        };
    const getProductGeoCoordinates =
      typeof deps?.getProductGeoCoordinates === "function"
        ? deps.getProductGeoCoordinates
        : function () {
          return null;
        };
    const isEmptyField =
      typeof deps?.isEmptyField === "function"
        ? deps.isEmptyField
        : function (value) {
          return value == null || value === "";
        };
    const resolveBoatPromoBadge =
      typeof deps?.resolveBoatPromoBadge === "function"
        ? deps.resolveBoatPromoBadge
        : function () {
          return null;
        };
    const parsePrice =
      typeof deps?.parsePrice === "function"
        ? deps.parsePrice
        : function (value) {
          return Number(value);
        };
    const parseGeoCoordinate =
      typeof deps?.parseGeoCoordinate === "function"
        ? deps.parseGeoCoordinate
        : function (value) {
          return Number(value);
        };
    const resolveProductCardLength =
      typeof deps?.resolveProductCardLength === "function"
        ? deps.resolveProductCardLength
        : null;
    const resolveProductCardCondition =
      typeof deps?.resolveProductCardCondition === "function"
        ? deps.resolveProductCardCondition
        : null;

    const PRODUCT_CARD_SECTION_ID = "product-card-search";
    const COVEO_LIQUID_PRODUCT_SECTION_FETCH_CONCURRENCY = 4;
    const COVEO_LIQUID_PRODUCT_SECTION_FETCH_CONCURRENCY_PROGRESSIVE = 4;
    const COVEO_LIQUID_PRODUCT_PROGRESSIVE_FLUSH_DELAY_MS = 24;
    const COVEO_LIQUID_PRODUCT_CARD_FAILURE_COOLDOWN_MS = 60 * 1000;
    const COVEO_LIQUID_PRODUCT_CARD_RATE_LIMIT_WINDOW_MS = 15 * 1000;
    const COVEO_LIQUID_PRODUCT_CARD_RATE_LIMIT_THRESHOLD = 3;
    const COVEO_LIQUID_PRODUCT_CARD_GLOBAL_SUSPEND_MS = 12 * 1000;
    const COVEO_LIQUID_PRODUCT_CARD_FETCH_TIMEOUT_MS = 8 * 1000;
    const COVEO_LIQUID_PRODUCT_CARD_MAX_ATTEMPTS = 3;
    const COVEO_LIQUID_PRODUCT_CARD_RETRY_SPACING_MS = 400;
    const COVEO_LIQUID_PRODUCT_CARD_NOT_FOUND_TTL_MS = 60 * 1000;
    const COVEO_LIQUID_PRODUCT_CARD_GONE_TTL_MS = 15 * 60 * 1000;
    const COVEO_LIQUID_PRODUCT_CARD_INACTIVE_TTL_MS = 5 * 60 * 1000;
    const COVEO_LIQUID_PRODUCT_CARD_SEARCH_HYDRATION_LIMIT =
      searchPageResultsPerPage;
    const COVEO_LIQUID_PRODUCT_CARD_FETCH_STATUS = Object.freeze({
      OK: "ok",
      CONFIRMED_UNAVAILABLE: "confirmed-unavailable",
      TRANSIENT_FAILURE: "transient-failure",
    });
    const coveoLiquidProductCardHtmlCache = new Map();
    const coveoLiquidProductCardUnavailableCache = new Map();
    const coveoLiquidProductCardInFlight = new Map();
    const coveoLiquidProductCardFailureCache = new Map();
    const coveoLiquidProductCardRecentFailures = [];
    const coveoLiquidProductCardFetchQueue = [];
    let coveoLiquidProductCardActiveFetches = 0;
    let coveoLiquidProductCardGlobalSuspendUntil = 0;
    let coveoLiquidProductCardNextRetryAt = 0;

    function canContinueLiquidProductCardFetch(check) {
      if (typeof check !== "function") return true;
      try {
        return check() !== false;
      } catch (_) {
        return false;
      }
    }

    function drainCoveoLiquidProductCardFetchQueue() {
      while (
        coveoLiquidProductCardActiveFetches <
          COVEO_LIQUID_PRODUCT_SECTION_FETCH_CONCURRENCY &&
        coveoLiquidProductCardFetchQueue.length
      ) {
        const job = coveoLiquidProductCardFetchQueue.shift();
        if (!canContinueLiquidProductCardFetch(job.shouldContinue)) {
          job.resolve(
            createLiquidProductCardFetchResult(
              COVEO_LIQUID_PRODUCT_CARD_FETCH_STATUS.TRANSIENT_FAILURE,
              {
                reason: "hydration-stale",
                path: job.path,
              },
            ),
          );
          continue;
        }
        coveoLiquidProductCardActiveFetches += 1;
        Promise.resolve()
          .then(job.task)
          .then(job.resolve, job.reject)
          .finally(function () {
            coveoLiquidProductCardActiveFetches -= 1;
            drainCoveoLiquidProductCardFetchQueue();
          });
      }
    }

    function scheduleCoveoLiquidProductCardFetch(path, task, shouldContinue) {
      return new Promise(function (resolve, reject) {
        coveoLiquidProductCardFetchQueue.push({
          path,
          reject,
          resolve,
          shouldContinue,
          task,
        });
        drainCoveoLiquidProductCardFetchQueue();
      });
    }

    function waitForLiquidProductCardDelay(delayMs, shouldContinue) {
      const delay = Math.max(0, Number(delayMs) || 0);
      if (delay === 0) {
        return Promise.resolve(
          canContinueLiquidProductCardFetch(shouldContinue),
        );
      }
      return new Promise(function (resolve) {
        const deadline = Date.now() + delay;
        const check = function () {
          if (!canContinueLiquidProductCardFetch(shouldContinue)) {
            resolve(false);
            return;
          }
          const remaining = deadline - Date.now();
          if (remaining <= 0) {
            resolve(true);
            return;
          }
          setTimeout(check, Math.min(250, remaining));
        };
        setTimeout(check, Math.min(250, delay));
      });
    }

    function getLiquidProductCardRetryDelayMs(response, attempt) {
      const fallback = 600 * (attempt + 1) + Math.floor(Math.random() * 250);
      const retryAfter = response?.headers?.get?.("retry-after");
      if (retryAfter == null || String(retryAfter).trim() === "") {
        return fallback;
      }
      const seconds = Number(retryAfter);
      if (Number.isFinite(seconds) && seconds >= 0) {
        return Math.max(fallback, Math.min(30000, seconds * 1000));
      }
      const retryAt = Date.parse(String(retryAfter));
      if (!Number.isFinite(retryAt)) return fallback;
      return Math.max(fallback, Math.min(30000, retryAt - Date.now()));
    }

    async function fetchLiquidProductCardSection(url, shouldContinue) {
      if (typeof AbortController !== "function") return fetch(url);
      if (!canContinueLiquidProductCardFetch(shouldContinue)) {
        const staleError = new Error("Card hydration is no longer current");
        staleError.coveoCardRequestStale = true;
        throw staleError;
      }
      const abortController = new AbortController();
      let abortReason = "";
      const timeoutId = setTimeout(function () {
        if (abortReason) return;
        abortReason = "timeout";
        abortController.abort();
      }, COVEO_LIQUID_PRODUCT_CARD_FETCH_TIMEOUT_MS);
      const staleCheckId =
        typeof shouldContinue === "function" &&
        typeof setInterval === "function"
          ? setInterval(function () {
            if (
              abortReason ||
              canContinueLiquidProductCardFetch(shouldContinue)
            ) {
              return;
            }
            abortReason = "stale";
            abortController.abort();
          }, 100)
          : null;
      try {
        return await fetch(url, { signal: abortController.signal });
      } catch (error) {
        if (abortReason === "stale") {
          const staleError = new Error("Card hydration became stale");
          staleError.coveoCardRequestStale = true;
          throw staleError;
        }
        if (abortReason === "timeout" && error && typeof error === "object") {
          try {
            error.coveoCardRequestTimedOut = true;
          } catch (_) { }
        }
        throw error;
      } finally {
        clearTimeout(timeoutId);
        if (staleCheckId != null && typeof clearInterval === "function") {
          clearInterval(staleCheckId);
        }
      }
    }

    async function waitForCoveoLiquidProductCardHydrationWindow(
      shouldContinue,
    ) {
      while (true) {
        if (!canContinueLiquidProductCardFetch(shouldContinue)) return false;
        const remaining = Math.max(
          0,
          coveoLiquidProductCardGlobalSuspendUntil - Date.now(),
        );
        if (remaining <= 0) return true;
        const mayContinue = await waitForLiquidProductCardDelay(
          remaining,
          shouldContinue,
        );
        if (!mayContinue) return false;
      }
    }

    async function waitForCoveoLiquidProductCardRetryTurn(shouldContinue) {
      if (!canContinueLiquidProductCardFetch(shouldContinue)) return false;
      const now = Date.now();
      const retryAt = Math.max(
        now,
        coveoLiquidProductCardGlobalSuspendUntil,
        coveoLiquidProductCardNextRetryAt,
      );
      coveoLiquidProductCardNextRetryAt =
        retryAt + COVEO_LIQUID_PRODUCT_CARD_RETRY_SPACING_MS;
      return waitForLiquidProductCardDelay(
        Math.max(0, retryAt - now),
        shouldContinue,
      );
    }

    async function waitForCoveoLiquidProductCardRetryRecoveryWindow(
      shouldContinue,
    ) {
      while (true) {
        if (
          !(await waitForCoveoLiquidProductCardHydrationWindow(
            shouldContinue,
          ))
        ) {
          return false;
        }
        if (
          !(await waitForCoveoLiquidProductCardRetryTurn(shouldContinue))
        ) {
          return false;
        }
        if (!(coveoLiquidProductCardGlobalSuspendUntil > Date.now())) {
          return canContinueLiquidProductCardFetch(shouldContinue);
        }
      }
    }

    function getLiquidProductCardHttpAttemptLimit(status) {
      if (status === 429 || status === 503) {
        return COVEO_LIQUID_PRODUCT_CARD_MAX_ATTEMPTS;
      }
      if (status === 408 || status === 425 || status >= 500) {
        return 2;
      }
      return 1;
    }

    function getLiquidProductCardUnavailableTtlMs(httpStatus) {
      if (httpStatus === 410) {
        return COVEO_LIQUID_PRODUCT_CARD_GONE_TTL_MS;
      }
      if (httpStatus === 404) {
        return COVEO_LIQUID_PRODUCT_CARD_NOT_FOUND_TTL_MS;
      }
      return COVEO_LIQUID_PRODUCT_CARD_INACTIVE_TTL_MS;
    }

    function cacheUnavailableLiquidProductCard(path, httpStatus, reason) {
      coveoLiquidProductCardUnavailableCache.set(path, {
        expiresAt:
          Date.now() + getLiquidProductCardUnavailableTtlMs(httpStatus),
        httpStatus,
        reason,
      });
    }

    function getCachedUnavailableLiquidProductCard(path) {
      const cached = coveoLiquidProductCardUnavailableCache.get(path);
      if (!cached) return null;
      if (Number(cached.expiresAt) > Date.now()) return cached;
      coveoLiquidProductCardUnavailableCache.delete(path);
      return null;
    }

    function isCoveoLiquidCardHydrationGloballySuspended() {
      if (!(coveoLiquidProductCardGlobalSuspendUntil > Date.now())) {
        coveoLiquidProductCardGlobalSuspendUntil = 0;
        return false;
      }
      return true;
    }

    function recordCoveoLiquidCardFailureStatus(status) {
      if (status !== 429 && status !== 503) return;
      const now = Date.now();
      coveoLiquidProductCardRecentFailures.push(now);
      while (
        coveoLiquidProductCardRecentFailures.length &&
        coveoLiquidProductCardRecentFailures[0] <
          now - COVEO_LIQUID_PRODUCT_CARD_RATE_LIMIT_WINDOW_MS
      ) {
        coveoLiquidProductCardRecentFailures.shift();
      }
      if (
        coveoLiquidProductCardRecentFailures.length >=
        COVEO_LIQUID_PRODUCT_CARD_RATE_LIMIT_THRESHOLD
      ) {
        coveoLiquidProductCardGlobalSuspendUntil =
          now + COVEO_LIQUID_PRODUCT_CARD_GLOBAL_SUSPEND_MS;
        coveoLiquidProductCardRecentFailures.length = 0;
      }
    }

    function shouldSkipLiquidCardHydrationForResult(container, index) {
      const isSearchPage = !!container?.closest?.("#coveo-headless-search");
      return (
        isSearchPage &&
        Number(index) >= COVEO_LIQUID_PRODUCT_CARD_SEARCH_HYDRATION_LIMIT
      );
    }

    function renderSkeletonCard() {
      return `<div class="f-column card coveo-search-card-wrapper" role="presentation" aria-hidden="true">
      <div class="coveo-search-card coveo-search-card--skeleton">
        <div class="coveo-search-card__link">
          <div class="coveo-search-card__image-wrap">
            <div class="coveo-skeleton coveo-skeleton--image no-empty" aria-hidden="true"></div>
          </div>
          <div class="coveo-search-card__info">
            <div class="coveo-search-card__top-row">
              <div class="coveo-search-card__meta">
                <span class="coveo-skeleton coveo-skeleton--meta" style="display: inline-block; width: 80px; height: 14px; vertical-align: middle;"></span>
              </div>
              <div class="coveo-search-card__tag-badges">
                <span class="coveo-skeleton coveo-skeleton--badge" style="display: inline-block; width: 70px; height: 24px; border-radius: 9999px; vertical-align: middle;"></span>
              </div>
            </div>
            <div class="coveo-search-card__heading-block">
              <div class="coveo-skeleton coveo-skeleton--title no-empty" style="width: 100%; max-width: 100%; min-height: 64px; height: 64px; border-radius: 8px;"></div>
            </div>
            <hr class="coveo-search-card__divider">
            <div class="coveo-search-card__footer">
              <div class="coveo-search-card__footer-main">
                <div class="coveo-search-card__price-wrap">
                  <div class="coveo-skeleton coveo-skeleton--price no-empty" style="width: 120px; height: 30px; border-radius: 6px;"></div>
                </div>
                <span class="coveo-skeleton coveo-skeleton--stock" style="display: inline-block; width: 72px; height: 14px; border-radius: 4px;"></span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>`;
    }

    function getProductPathFromClickUri(clickUri) {
      if (!clickUri || typeof clickUri !== "string") return null;
      const s = String(clickUri).trim();
      if (s.startsWith("/")) return s.split("?")[0];
      try {
        const url = new URL(s, window.location.origin);
        return url.pathname.split("?")[0];
      } catch (_) {
        return null;
      }
    }

    function getCoveoStableProductKey(p) {
      if (!p || typeof p !== "object") return "";
      if (p.permanentid != null && String(p.permanentid).trim()) {
        return String(p.permanentid).trim();
      }
      if (p.ec_product_id != null && String(p.ec_product_id).trim()) {
        return String(p.ec_product_id).trim();
      }
      const alt = getProductField(p, ["ec_product_id", "permanentid"]);
      if (alt != null && String(alt).trim()) return String(alt).trim();
      if (p.clickUri) return String(p.clickUri).trim();
      return "";
    }

    function applyMissingCoveoDealerAttrsToCardElement(cardEl, product) {
      if (!cardEl || !product || typeof product !== "object") return;
      try {
        const coords = getProductGeoCoordinates(product);
        if (coords && !cardEl.getAttribute("data-dealer-lat")) {
          cardEl.setAttribute("data-dealer-lat", String(coords.lat));
        }
        if (coords && !cardEl.getAttribute("data-dealer-lng")) {
          cardEl.setAttribute("data-dealer-lng", String(coords.lng));
        }

        const dealerCity = getProductField(product, [
          "iiq.city",
          "iiq_city",
          "ec_city",
          "city",
        ]);
        const dealerState = getProductField(product, [
          "iiq.state",
          "iiq_state",
          "ec_state",
          "state",
        ]);
        const dealerZip = getProductField(product, [
          "iiq-dealer.zip",
          "iiq_dealer_zip",
          "iiq-dealer_zip",
          "dealer_zip",
          "ec_dealer_zip",
          "postal_code",
          "zip",
        ]);
        const dealerPlace = getProductField(product, [
          "location.name",
          "location_name",
          "iiq-dealer.name",
          "iiq_dealer_name",
          "dealer_name",
        ]);

        if (dealerCity && !cardEl.getAttribute("data-dealer-city")) {
          cardEl.setAttribute("data-dealer-city", String(dealerCity));
        }
        if (dealerState && !cardEl.getAttribute("data-dealer-state")) {
          cardEl.setAttribute("data-dealer-state", String(dealerState));
        }
        if (dealerZip && !cardEl.getAttribute("data-dealer-zip")) {
          cardEl.setAttribute("data-dealer-zip", String(dealerZip));
        }
        if (dealerPlace && !cardEl.getAttribute("data-dealer-place")) {
          cardEl.setAttribute("data-dealer-place", String(dealerPlace));
        }
      } catch (_) { }
    }

    function getFallbackProductLength(product) {
      if (resolveProductCardLength) {
        return resolveProductCardLength(product);
      }
      return getProductField(product, [
        "ec_length_nominal",
        "ec_custom_field_length_nominal",
        "ec_custom_field_iiq-length_nominal",
        "iiq_length_nominal",
        "iiq.length_nominal",
        "iiq-length_nominal",
        "length_nominal",
        "lengthnominal",
      ]);
    }

    function getFallbackProductCondition(product) {
      if (resolveProductCardCondition) {
        return resolveProductCardCondition(product);
      }
      return getProductField(product, [
        "condition_iiq",
        "ec_condition",
        "ec_boat_status",
        "ec_custom_field_condition_iiq",
        "ec_custom_field_iiq-boat_status",
        "iiq_condition",
        "iiq.condition",
        "iiq.boat_status",
        "iiq_boat_status",
        "iiq-boat_status",
        "boat_status",
        "condition",
      ]);
    }

    function getFallbackProductStockNumber(product) {
      const raw = getProductField(product, [
        "iiq_stock_number",
        "iiq.stock_number",
        "stock_number",
        "ec_sku",
        "sku",
      ]);
      if (!raw) return "";
      const normalized = String(raw).trim().replace(/^#/, "").trim();
      if (!normalized || /^\d{12,}$/.test(normalized)) return "";
      return normalized.includes("/")
        ? normalized.split("/").pop().trim()
        : normalized;
    }

    function formatFallbackLengthValue(value) {
      if (!value) return "";
      const raw = String(value).trim();
      if (!raw) return "";
      return /['"]$/.test(raw) ? raw : `${raw}'`;
    }

    function ensureCardMetaSpan(doc, metaEl, className, text) {
      if (!doc || !metaEl || !className || !text) return null;
      let el = metaEl.querySelector(`.${className}`);
      if (!el) {
        el = doc.createElement("span");
        el.className = className;
        metaEl.appendChild(el);
      }
      el.textContent = text;
      return el;
    }

    function applyMissingSearchCardSummaryToCardElement(doc, cardEl, product) {
      if (!doc || !cardEl || !product || typeof product !== "object") return;
      try {
        const metaEl = cardEl.querySelector(".coveo-search-card__meta");
        if (metaEl) {
          const existingLengthEl = metaEl.querySelector(
            ".coveo-search-card__length",
          );
          const existingConditionEl = metaEl.querySelector(
            ".coveo-search-card__condition",
          );
          const hasLength = !!String(
            existingLengthEl?.textContent || "",
          ).trim();
          const hasCondition = !!String(
            existingConditionEl?.textContent || "",
          ).trim();
          const fallbackLength = formatFallbackLengthValue(
            getFallbackProductLength(product),
          );
          const fallbackCondition = String(
            getFallbackProductCondition(product) || "",
          ).trim();

          if (!hasLength && fallbackLength) {
            ensureCardMetaSpan(
              doc,
              metaEl,
              "coveo-search-card__length",
              fallbackLength,
            );
          }
          if (!hasCondition && fallbackCondition) {
            ensureCardMetaSpan(
              doc,
              metaEl,
              "coveo-search-card__condition",
              fallbackCondition,
            );
          }

          const lengthEl = metaEl.querySelector(".coveo-search-card__length");
          const conditionEl = metaEl.querySelector(".coveo-search-card__condition");
          let sepEl = metaEl.querySelector(".coveo-search-card__meta-sep");
          if (lengthEl && conditionEl) {
            if (!sepEl) {
              sepEl = doc.createElement("span");
              sepEl.className = "coveo-search-card__meta-sep";
              sepEl.textContent = "|";
              conditionEl.before(sepEl);
            } else {
              sepEl.textContent = "|";
            }
          } else if (sepEl) {
            sepEl.remove();
          }
        }

        const stockEl = cardEl.querySelector(".coveo-search-card__stock-id");
        const fallbackStock = getFallbackProductStockNumber(product);
        if (
          stockEl &&
          fallbackStock &&
          (!String(stockEl.textContent || "").trim() ||
            stockEl.classList.contains("coveo-search-card__stock-id--placeholder"))
        ) {
          stockEl.textContent = `# ${fallbackStock}`;
          stockEl.classList.remove("coveo-search-card__stock-id--placeholder");
          stockEl.removeAttribute("aria-hidden");
        }
      } catch (_) { }
    }

    function attachCoveoProductKeyToCardHtml(html, key, product) {
      if (!html) return html;
      try {
        const doc = new DOMParser().parseFromString(String(html).trim(), "text/html");
        const wrapper =
          doc.querySelector(".coveo-search-card-wrapper") ||
          doc.querySelector(".f-column.card");
        const root =
          wrapper ||
          doc.querySelector(".coveo-search-card") ||
          doc.querySelector(".coveo-boat-card");
        if (!root) return html;
        if (key) {
          root.setAttribute("data-coveo-product-key", key);
        }
        const cardEl =
          root.matches(".coveo-search-card, .coveo-boat-card")
            ? root
            : root.querySelector(".coveo-search-card, .coveo-boat-card");
        applyMissingCoveoDealerAttrsToCardElement(cardEl, product);
        applyMissingSearchCardSummaryToCardElement(doc, cardEl, product);
        return root.outerHTML;
      } catch (_) {
        return html;
      }
    }

    function injectCoveoPromoIntoSearchCardHtml(html, p) {
      if (!html || !p || typeof p !== "object") return html;
      let doc;
      try {
        doc = new DOMParser().parseFromString(html, "text/html");
      } catch (_) {
        return html;
      }
      const card = doc.querySelector(".coveo-search-card");
      // Liquid already rendered the current Shopify metafield, even when empty.
      // An older search-index value must not add or resurrect a promotion.
      if (card?.hasAttribute("data-iiq-promotion-name")) return html;

      let promotionBadge;
      try {
        promotionBadge = resolveBoatPromoBadge(p);
      } catch (_) {
        return html;
      }
      const topRow = doc.querySelector(".coveo-search-card__top-row");
      if (!topRow) return html;

      let badges = topRow.querySelector(".coveo-search-card__tag-badges");
      if (!badges && promotionBadge) {
        badges = doc.createElement("div");
        badges.className = "coveo-search-card__tag-badges";
        topRow.appendChild(badges);
      }

      // Unmarked fallback cards may contain old tag badges; never reuse their text.
      if (badges) badges.replaceChildren();
      if (promotionBadge) {
        const span = doc.createElement("span");
        span.className =
          "coveo-search-card__tag-badge coveo-search-card__tag-badge--" +
          promotionBadge.modifier;
        span.setAttribute("data-coveo-resolved-promo", "1");
        const labelSpan = doc.createElement("span");
        labelSpan.className = "coveo-search-card__tag-badge-text";
        labelSpan.textContent = promotionBadge.label;
        span.appendChild(labelSpan);
        badges.appendChild(span);
      }

      const root =
        doc.querySelector(".coveo-search-card-wrapper") ||
        doc.querySelector(".f-column.card") ||
        doc.querySelector(".coveo-search-card") ||
        doc.body.firstElementChild;
      return root ? root.outerHTML : html;
    }

    function createLiquidProductCardFetchResult(status, options) {
      const details = options || {};
      return {
        status,
        html: typeof details.html === "string" ? details.html : "",
        httpStatus: Number.isFinite(details.httpStatus)
          ? details.httpStatus
          : null,
        reason: String(details.reason || ""),
        path: String(details.path || ""),
      };
    }

    function classifyLiquidProductCardHtml(html, path, httpStatus) {
      if (!html || typeof html !== "string") {
        return createLiquidProductCardFetchResult(
          COVEO_LIQUID_PRODUCT_CARD_FETCH_STATUS.TRANSIENT_FAILURE,
          {
            httpStatus,
            reason: "empty-section-response",
            path,
          },
        );
      }

      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const ghostPlaceholder = doc.querySelector(
          "[data-mm-coveo-ghost-placeholder='1']",
        );
        if (ghostPlaceholder) {
          return createLiquidProductCardFetchResult(
            COVEO_LIQUID_PRODUCT_CARD_FETCH_STATUS.CONFIRMED_UNAVAILABLE,
            {
              html,
              httpStatus,
              reason: "liquid-ghost-placeholder",
              path,
            },
          );
        }

        const card = doc.querySelector(".coveo-search-card");
        if (card) {
          return createLiquidProductCardFetchResult(
            COVEO_LIQUID_PRODUCT_CARD_FETCH_STATUS.OK,
            {
              html,
              httpStatus,
              reason: "liquid-card",
              path,
            },
          );
        }

        const cardWrapper = doc.querySelector(".coveo-search-card-wrapper");
        if (cardWrapper) {
          return createLiquidProductCardFetchResult(
            COVEO_LIQUID_PRODUCT_CARD_FETCH_STATUS.CONFIRMED_UNAVAILABLE,
            {
              html,
              httpStatus,
              reason: "liquid-inactive-product",
              path,
            },
          );
        }
      } catch (_) { }

      return createLiquidProductCardFetchResult(
        COVEO_LIQUID_PRODUCT_CARD_FETCH_STATUS.TRANSIENT_FAILURE,
        {
          html,
          httpStatus,
          reason: "unexpected-section-markup",
          path,
        },
      );
    }

    async function fetchLiquidProductCardSectionResult(path, options) {
      const fetchOptions = options || {};
      if (!path || !path.startsWith("/products/")) {
        return createLiquidProductCardFetchResult(
          COVEO_LIQUID_PRODUCT_CARD_FETCH_STATUS.TRANSIENT_FAILURE,
          {
            reason: "invalid-product-path",
            path,
          },
        );
      }
      if (coveoLiquidProductCardHtmlCache.has(path)) {
        return createLiquidProductCardFetchResult(
          COVEO_LIQUID_PRODUCT_CARD_FETCH_STATUS.OK,
          {
            html: coveoLiquidProductCardHtmlCache.get(path) || "",
            reason: "html-cache",
            path,
          },
        );
      }
      const cachedUnavailable = getCachedUnavailableLiquidProductCard(path);
      if (cachedUnavailable) {
        return createLiquidProductCardFetchResult(
          COVEO_LIQUID_PRODUCT_CARD_FETCH_STATUS.CONFIRMED_UNAVAILABLE,
          {
            httpStatus: cachedUnavailable?.httpStatus,
            reason: cachedUnavailable?.reason || "product-not-found-cache",
            path,
          },
        );
      }
      const now = Date.now();
      const recentFailureExpiry = coveoLiquidProductCardFailureCache.get(path) || 0;
      if (recentFailureExpiry > now) {
        return createLiquidProductCardFetchResult(
          COVEO_LIQUID_PRODUCT_CARD_FETCH_STATUS.TRANSIENT_FAILURE,
          {
            reason: "failure-cooldown",
            path,
          },
        );
      }
      if (recentFailureExpiry) {
        coveoLiquidProductCardFailureCache.delete(path);
      }
      if (coveoLiquidProductCardInFlight.has(path)) {
        const existing = coveoLiquidProductCardInFlight.get(path);
        if (typeof fetchOptions.shouldContinue === "function") {
          existing.continuationChecks.push(fetchOptions.shouldContinue);
        } else {
          existing.hasUnconditionalConsumer = true;
        }
        return existing.promise;
      }
      const url = path + "?section_id=" + PRODUCT_CARD_SECTION_ID;
      const maxAttempts = COVEO_LIQUID_PRODUCT_CARD_MAX_ATTEMPTS;
      const inFlightEntry = {
        continuationChecks: [],
        hasUnconditionalConsumer:
          typeof fetchOptions.shouldContinue !== "function",
        promise: null,
      };
      if (typeof fetchOptions.shouldContinue === "function") {
        inFlightEntry.continuationChecks.push(fetchOptions.shouldContinue);
      }
      const hasActiveConsumer = function () {
        return (
          inFlightEntry.hasUnconditionalConsumer ||
          inFlightEntry.continuationChecks.some(
            canContinueLiquidProductCardFetch,
          )
        );
      };
      const req = scheduleCoveoLiquidProductCardFetch(path, async function () {
        let lastHttpStatus = null;
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
          const mayContinue =
            attempt > 0
              ? await waitForCoveoLiquidProductCardRetryRecoveryWindow(
                hasActiveConsumer,
              )
              : await waitForCoveoLiquidProductCardHydrationWindow(
                hasActiveConsumer,
              );
          if (!mayContinue) {
            return createLiquidProductCardFetchResult(
              COVEO_LIQUID_PRODUCT_CARD_FETCH_STATUS.TRANSIENT_FAILURE,
              {
                httpStatus: lastHttpStatus,
                reason: "hydration-stale",
                path,
              },
            );
          }
          try {
            const res = await fetchLiquidProductCardSection(
              url,
              hasActiveConsumer,
            );
            lastHttpStatus = res.status;
            if (res.status === 429 || res.status === 503) {
              recordCoveoLiquidCardFailureStatus(res.status);
            }
            if (res.status === 404 || res.status === 410) {
              cacheUnavailableLiquidProductCard(
                path,
                res.status,
                "product-not-found",
              );
              return createLiquidProductCardFetchResult(
                COVEO_LIQUID_PRODUCT_CARD_FETCH_STATUS.CONFIRMED_UNAVAILABLE,
                {
                  httpStatus: res.status,
                  reason: "product-not-found",
                  path,
                },
              );
            }
            if (
              (res.status === 429 || res.status === 503) &&
              attempt + 1 < getLiquidProductCardHttpAttemptLimit(res.status)
            ) {
              const mayRetry = await waitForLiquidProductCardDelay(
                getLiquidProductCardRetryDelayMs(res, attempt),
                hasActiveConsumer,
              );
              if (!mayRetry) {
                return createLiquidProductCardFetchResult(
                  COVEO_LIQUID_PRODUCT_CARD_FETCH_STATUS.TRANSIENT_FAILURE,
                  {
                    httpStatus: lastHttpStatus,
                    reason: "hydration-stale",
                    path,
                  },
                );
              }
              continue;
            }
            if (!res.ok) {
              if (
                attempt + 1 <
                getLiquidProductCardHttpAttemptLimit(res.status)
              ) {
                const mayRetry = await waitForLiquidProductCardDelay(
                  220 * (attempt + 1),
                  hasActiveConsumer,
                );
                if (!mayRetry) {
                  return createLiquidProductCardFetchResult(
                    COVEO_LIQUID_PRODUCT_CARD_FETCH_STATUS.TRANSIENT_FAILURE,
                    {
                      httpStatus: lastHttpStatus,
                      reason: "hydration-stale",
                      path,
                    },
                  );
                }
                continue;
              }
              coveoLiquidProductCardFailureCache.set(
                path,
                Date.now() + COVEO_LIQUID_PRODUCT_CARD_FAILURE_COOLDOWN_MS,
              );
              return createLiquidProductCardFetchResult(
                COVEO_LIQUID_PRODUCT_CARD_FETCH_STATUS.TRANSIENT_FAILURE,
                {
                  httpStatus: res.status,
                  reason: "http-error",
                  path,
                },
              );
            }
            const html = (await res.text()).trim();
            if (!html || /Liquid error/i.test(html)) {
              if (attempt < 1) {
                const mayRetry = await waitForLiquidProductCardDelay(
                  220 * (attempt + 1),
                  hasActiveConsumer,
                );
                if (!mayRetry) {
                  return createLiquidProductCardFetchResult(
                    COVEO_LIQUID_PRODUCT_CARD_FETCH_STATUS.TRANSIENT_FAILURE,
                    {
                      httpStatus: lastHttpStatus,
                      reason: "hydration-stale",
                      path,
                    },
                  );
                }
                continue;
              }
              coveoLiquidProductCardFailureCache.set(
                path,
                Date.now() + COVEO_LIQUID_PRODUCT_CARD_FAILURE_COOLDOWN_MS,
              );
              return createLiquidProductCardFetchResult(
                COVEO_LIQUID_PRODUCT_CARD_FETCH_STATUS.TRANSIENT_FAILURE,
                {
                  httpStatus: res.status,
                  reason: !html ? "empty-section-response" : "liquid-error",
                  path,
                },
              );
            }

            const classified = classifyLiquidProductCardHtml(
              html,
              path,
              res.status,
            );
            if (
              classified.status ===
              COVEO_LIQUID_PRODUCT_CARD_FETCH_STATUS.TRANSIENT_FAILURE
            ) {
              if (attempt < 1) {
                const mayRetry = await waitForLiquidProductCardDelay(
                  220 * (attempt + 1),
                  hasActiveConsumer,
                );
                if (!mayRetry) {
                  return createLiquidProductCardFetchResult(
                    COVEO_LIQUID_PRODUCT_CARD_FETCH_STATUS.TRANSIENT_FAILURE,
                    {
                      httpStatus: lastHttpStatus,
                      reason: "hydration-stale",
                      path,
                    },
                  );
                }
                continue;
              }
              coveoLiquidProductCardFailureCache.set(
                path,
                Date.now() + COVEO_LIQUID_PRODUCT_CARD_FAILURE_COOLDOWN_MS,
              );
              return classified;
            }

            if (
              classified.status ===
              COVEO_LIQUID_PRODUCT_CARD_FETCH_STATUS.CONFIRMED_UNAVAILABLE
            ) {
              cacheUnavailableLiquidProductCard(
                path,
                res.status,
                classified.reason,
              );
              coveoLiquidProductCardFailureCache.delete(path);
              return classified;
            }

            coveoLiquidProductCardFailureCache.delete(path);
            coveoLiquidProductCardHtmlCache.set(path, html);
            return createLiquidProductCardFetchResult(
              COVEO_LIQUID_PRODUCT_CARD_FETCH_STATUS.OK,
              {
                html,
                httpStatus: res.status,
                reason: "section-response",
                path,
              },
            );
          } catch (error) {
            if (error?.coveoCardRequestStale) {
              return createLiquidProductCardFetchResult(
                COVEO_LIQUID_PRODUCT_CARD_FETCH_STATUS.TRANSIENT_FAILURE,
                {
                  httpStatus: lastHttpStatus,
                  reason: "hydration-stale",
                  path,
                },
              );
            }
            if (attempt >= 1) {
              coveoLiquidProductCardFailureCache.set(
                path,
                Date.now() + COVEO_LIQUID_PRODUCT_CARD_FAILURE_COOLDOWN_MS,
              );
              return createLiquidProductCardFetchResult(
                COVEO_LIQUID_PRODUCT_CARD_FETCH_STATUS.TRANSIENT_FAILURE,
                {
                  httpStatus: lastHttpStatus,
                  reason: error?.coveoCardRequestTimedOut
                    ? "request-timeout"
                    : "network-error",
                  path,
                },
              );
            }
            const mayRetry = await waitForLiquidProductCardDelay(
              220 * (attempt + 1),
              hasActiveConsumer,
            );
            if (!mayRetry) {
              return createLiquidProductCardFetchResult(
                COVEO_LIQUID_PRODUCT_CARD_FETCH_STATUS.TRANSIENT_FAILURE,
                {
                  httpStatus: lastHttpStatus,
                  reason: "hydration-stale",
                  path,
                },
              );
            }
          }
        }
        return createLiquidProductCardFetchResult(
          COVEO_LIQUID_PRODUCT_CARD_FETCH_STATUS.TRANSIENT_FAILURE,
          {
            httpStatus: lastHttpStatus,
            reason: "attempts-exhausted",
            path,
          },
        );
      }, hasActiveConsumer);
      inFlightEntry.promise = req;
      coveoLiquidProductCardInFlight.set(path, inFlightEntry);
      try {
        return await req;
      } finally {
        if (coveoLiquidProductCardInFlight.get(path) === inFlightEntry) {
          coveoLiquidProductCardInFlight.delete(path);
        }
      }
    }

    async function fetchLiquidProductCardSectionHtml(path) {
      const result = await fetchLiquidProductCardSectionResult(path);
      return result.html || "";
    }

    async function fetchLiquidProductCardResult(clickUri, p, options) {
      const path = getProductPathFromClickUri(clickUri);
      if (!path || !path.startsWith("/products/")) {
        return createLiquidProductCardFetchResult(
          COVEO_LIQUID_PRODUCT_CARD_FETCH_STATUS.TRANSIENT_FAILURE,
          {
            reason: "invalid-product-path",
            path,
          },
        );
      }

      const sectionResult = await fetchLiquidProductCardSectionResult(
        path,
        options,
      );
      if (sectionResult.status !== COVEO_LIQUID_PRODUCT_CARD_FETCH_STATUS.OK) {
        return sectionResult;
      }

      const merged =
        p && typeof p === "object"
          ? injectCoveoPromoIntoSearchCardHtml(sectionResult.html, p)
          : sectionResult.html;
      const normalizedHtml = stripSearchCardTrailingZeroCents(merged);
      return classifyLiquidProductCardHtml(
        normalizedHtml,
        path,
        sectionResult.httpStatus,
      );
    }

    async function fetchLiquidProductCard(clickUri, p, fallbackCardFn) {
      const fb = () => (fallbackCardFn ? fallbackCardFn() : "");
      const result = await fetchLiquidProductCardResult(clickUri, p);
      if (
        result.status !== COVEO_LIQUID_PRODUCT_CARD_FETCH_STATUS.OK ||
        !result.html
      ) {
        return fb();
      }
      return result.html;
    }

    function stripSearchCardTrailingZeroCents(html) {
      if (!html || typeof html !== "string") return html;
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const card = doc.querySelector(".coveo-search-card");
      if (!card) return html;

      const walker = doc.createTreeWalker(card, NodeFilter.SHOW_TEXT);
      let textNode = walker.nextNode();
      while (textNode) {
        const raw = textNode.nodeValue;
        if (raw && raw.includes(".00")) {
          textNode.nodeValue = raw.replace(/(\d)\.00(?!\d)/g, "$1");
        }
        textNode = walker.nextNode();
      }

      const root =
        doc.querySelector(".coveo-search-card-wrapper") ||
        doc.querySelector(".f-column.card") ||
        card;
      return root ? root.outerHTML : html;
    }

    function cacheProductGeoFromCardHtml(stableKey, html) {
      if (!stableKey || !html) return;
      try {
        const tmp = document.createElement("div");
        tmp.innerHTML = String(html);
        const card = tmp.querySelector(".coveo-search-card, .coveo-boat-card");
        if (!card) return;
        const lat = parseGeoCoordinate(card.getAttribute("data-dealer-lat"));
        const lng = parseGeoCoordinate(card.getAttribute("data-dealer-lng"));
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        if (!window.__coveoProductGeoByKey) {
          window.__coveoProductGeoByKey = new Map();
        }
        window.__coveoProductGeoByKey.set(stableKey, { lat, lng });
      } catch (_) { }
    }

    return {
      PRODUCT_CARD_SECTION_ID,
      COVEO_LIQUID_PRODUCT_CARD_FAILURE_COOLDOWN_MS,
      COVEO_LIQUID_PRODUCT_CARD_FETCH_TIMEOUT_MS,
      COVEO_LIQUID_PRODUCT_CARD_FETCH_STATUS,
      COVEO_LIQUID_PRODUCT_CARD_GLOBAL_SUSPEND_MS,
      COVEO_LIQUID_PRODUCT_CARD_GONE_TTL_MS,
      COVEO_LIQUID_PRODUCT_CARD_INACTIVE_TTL_MS,
      COVEO_LIQUID_PRODUCT_CARD_MAX_ATTEMPTS,
      COVEO_LIQUID_PRODUCT_CARD_NOT_FOUND_TTL_MS,
      COVEO_LIQUID_PRODUCT_CARD_RETRY_SPACING_MS,
      COVEO_LIQUID_PRODUCT_CARD_RATE_LIMIT_THRESHOLD,
      COVEO_LIQUID_PRODUCT_CARD_RATE_LIMIT_WINDOW_MS,
      COVEO_LIQUID_PRODUCT_CARD_SEARCH_HYDRATION_LIMIT,
      COVEO_LIQUID_PRODUCT_PROGRESSIVE_FLUSH_DELAY_MS,
      COVEO_LIQUID_PRODUCT_SECTION_FETCH_CONCURRENCY,
      COVEO_LIQUID_PRODUCT_SECTION_FETCH_CONCURRENCY_PROGRESSIVE,
      applyMissingCoveoDealerAttrsToCardElement,
      attachCoveoProductKeyToCardHtml,
      cacheProductGeoFromCardHtml,
      fetchLiquidProductCard,
      fetchLiquidProductCardResult,
      fetchLiquidProductCardSectionHtml,
      fetchLiquidProductCardSectionResult,
      getCoveoStableProductKey,
      getProductCardCondition: getFallbackProductCondition,
      getProductCardLength: getFallbackProductLength,
      getProductPathFromClickUri,
      injectCoveoPromoIntoSearchCardHtml,
      isCoveoLiquidCardHydrationGloballySuspended,
      recordCoveoLiquidCardFailureStatus,
      renderSkeletonCard,
      shouldSkipLiquidCardHydrationForResult,
      stripSearchCardTrailingZeroCents,
      waitForCoveoLiquidProductCardRetryRecoveryWindow,
    };
  }

  window.CoveoCardsModule = {
    create: createCoveoCardsModule,
  };
})();
